import { normalizeDocument } from '@crm/documents';
import { format } from '@react-input/mask';

/**
 * Brazilian document, phone and postal code patterns. Forms keep these values without
 * punctuation — digits, plus the upper-case letters an alphanumeric CNPJ may hold; the text
 * the reader sees is formatted here — by `MaskedInput` while typing and by the `format*`
 * helpers on read-only screens. Both use the same masks, so a value typed in a field reads
 * the same everywhere else.
 */

/** The replacement character of the masks below: `_` is one digit. */
export const MASK_DIGIT = { _: /\d/ } as const;
/** CNPJ masks also have `#`: a digit or a letter (the alphanumeric CNPJ). */
const DOCUMENT_REPLACEMENT = { _: /\d/, '#': /[0-9A-Za-z]/ } as const;

export const CEP_MASK = '_____-___';
export const LANDLINE_MASK = '(__) ____-____';
export const MOBILE_MASK = '(__) _____-____';
export const CPF_MASK = '___.___.___-__';
/** The first 12 characters of a CNPJ may be letters; the 2 check digits are numeric. */
export const CNPJ_MASK = '##.###.###/####-__';
/** A time of day: the first digit of the hour is 0–2 and of the minutes 0–5 while typing. */
export const TIME_MASK = 'Hh:Mm';
const TIME_REPLACEMENT = { H: /[0-2]/, h: /\d/, M: /[0-5]/, m: /\d/ } as const;

export const CEP_LENGTH = 8;
export const LANDLINE_LENGTH = 10;
export const MOBILE_LENGTH = 11;
export const CPF_LENGTH = 11;
export const CNPJ_LENGTH = 14;
export const TIME_LENGTH = 4;

export type MaskPreset = 'cep' | 'phone' | 'cpf' | 'cnpj' | 'cpfCnpj' | 'time';

/** The most characters (digits, or letters of a CNPJ) a value of the preset holds. */
export const MASK_MAX_DIGITS: Record<MaskPreset, number> = {
  cep: CEP_LENGTH,
  phone: MOBILE_LENGTH,
  cpf: CPF_LENGTH,
  cnpj: CNPJ_LENGTH,
  cpfCnpj: CNPJ_LENGTH,
  time: TIME_LENGTH,
};

/** `"(11) 3333-4444"` → `"1133334444"`. */
export function onlyDigits(value: string | null | undefined): string {
  return (value ?? '').replace(/\D/g, '');
}

/**
 * The digits of a Brazilian phone without the country code: `"+55 11 3333-4444"` →
 * `"1133334444"`. Phones saved as free text before the mask existed load this way.
 */
export function nationalPhoneDigits(value: string | null | undefined): string {
  const digits = onlyDigits(value);
  const withCountryCode = /^\s*\+\s*55/.test(value ?? '') && digits.length > MOBILE_LENGTH;
  return withCountryCode ? digits.slice(2) : digits;
}

/** Whether the preset takes the letters of an alphanumeric CNPJ. */
export function presetAcceptsLetters(preset: MaskPreset): boolean {
  return preset === 'cnpj' || preset === 'cpfCnpj';
}

/**
 * The value a field of the preset keeps from what was typed: the digits, or for a CNPJ the
 * digits and letters in upper case (`"12.abc.345/01de-35"` → `"12ABC34501DE35"`).
 */
export function cleanForPreset(preset: MaskPreset, value: string | null | undefined): string {
  return presetAcceptsLetters(preset) ? normalizeDocument(value) : onlyDigits(value);
}

/**
 * The mask for a value of `digitCount` characters (how many there are picks the dynamic
 * ones): a phone becomes a mobile at the 11th digit, a CPF becomes a CNPJ at the 12th
 * character — or at the first letter, which only a CNPJ has.
 */
export function maskFor(preset: MaskPreset, digitCount: number, hasLetters = false): string {
  switch (preset) {
    case 'cep':
      return CEP_MASK;
    case 'phone':
      return digitCount > LANDLINE_LENGTH ? MOBILE_MASK : LANDLINE_MASK;
    case 'cpf':
      return CPF_MASK;
    case 'cnpj':
      return CNPJ_MASK;
    case 'cpfCnpj':
      return digitCount > CPF_LENGTH || hasLetters ? CNPJ_MASK : CPF_MASK;
    case 'time':
      return TIME_MASK;
  }
}

/** Which character each mask slot accepts. */
export function replacementFor(preset: MaskPreset): Record<string, RegExp> {
  if (preset === 'time') return TIME_REPLACEMENT;
  return presetAcceptsLetters(preset) ? DOCUMENT_REPLACEMENT : MASK_DIGIT;
}

/**
 * Formats the digits (and CNPJ letters) of `value` with the preset's mask, as far as they go
 * (`"0131"` → `"0131"`, `"013101"` → `"01310-1"`): exactly the text the masked field shows
 * after typing them. A value with more characters than the pattern holds is not ours to cut
 * — it comes back as it was.
 */
export function formatWithPreset(preset: MaskPreset, value: string | null | undefined): string {
  const characters = cleanForPreset(preset, value);
  if (characters.length > MASK_MAX_DIGITS[preset]) return value ?? '';
  return format(characters, {
    mask: maskFor(preset, characters.length, /[A-Z]/.test(characters)),
    replacement: replacementFor(preset),
  });
}

/** What a masked field shows and keeps after an edit, and where its caret goes. */
export interface MaskedEdit {
  /** The formatted text. */
  text: string;
  /** The digits (and the upper-case letters of a CNPJ) the field keeps. */
  value: string;
  /** Where the caret goes in `text`. */
  caret: number;
}

/**
 * Fits what a masked field holds after an edit — `raw`, with the caret at `caret` — to the
 * preset's mask, synchronously: the characters the preset takes are kept in order (letters
 * upper-cased for a CNPJ), each lands in the next slot that accepts it (one that does not fit
 * its slot is dropped, as an hour starting with 9), and the caret stays after the same
 * character it followed. No literal is left dangling at the end.
 */
export function applyMask(preset: MaskPreset, raw: string, caret = raw.length): MaskedEdit {
  const letters = presetAcceptsLetters(preset);
  const keep = letters ? /[0-9A-Za-z]/ : /\d/;
  const chars: string[] = [];
  let before = 0;
  for (let index = 0; index < raw.length; index += 1) {
    const character = raw.charAt(index);
    if (!keep.test(character)) continue;
    chars.push(letters ? character.toUpperCase() : character);
    if (index < caret) before += 1;
  }
  const kept = chars.slice(0, MASK_MAX_DIGITS[preset]);
  const mask = maskFor(
    preset,
    kept.length,
    kept.some((character) => /[A-Z]/.test(character)),
  );
  const replacement = replacementFor(preset);
  let text = '';
  let value = '';
  let caretAt = 0;
  let next = 0;
  for (const slot of mask) {
    if (next >= kept.length) break;
    const accepts = replacement[slot];
    if (!accepts) {
      text += slot;
      continue;
    }
    while (next < kept.length && !accepts.test(kept[next] ?? '')) next += 1;
    if (next >= kept.length) break;
    text += kept[next];
    value += kept[next];
    if (next < before) caretAt = text.length;
    next += 1;
  }
  // A literal is only shown when a character follows it.
  while (text.length > 0 && !keep.test(text.charAt(text.length - 1))) text = text.slice(0, -1);
  caretAt = Math.min(caretAt, text.length);
  // Typing goes on after the literals that follow (`(11` → `(11) |`).
  while (caretAt < text.length && !keep.test(text.charAt(caretAt)) && before > 0) caretAt += 1;
  if (before === 0) caretAt = 0;
  return { text, value, caret: caretAt };
}

/** `"01310100"` → `"01310-100"`. */
export const formatCep = (value: string | null | undefined) => formatWithPreset('cep', value);

/** `"1133334444"` → `"(11) 3333-4444"`; `"11933334444"` → `"(11) 93333-4444"`. */
export const formatPhone = (value: string | null | undefined) => formatWithPreset('phone', value);

/** `"12345678909"` → `"123.456.789-09"`. */
export const formatCpf = (value: string | null | undefined) => formatWithPreset('cpf', value);

/** `"11222333000181"` → `"11.222.333/0001-81"`; `"12ABC34501DE35"` → `"12.ABC.345/01DE-35"`. */
export const formatCnpj = (value: string | null | undefined) => formatWithPreset('cnpj', value);

/** `"0830"` → `"08:30"`; a partial value goes as far as it goes (`"083"` → `"08:3"`). */
export const formatTime = (value: string | null | undefined) => formatWithPreset('time', value);
